import { ICONS } from '@/constants/constants';
import Image from 'next/image';
import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Tooltip as ReactTooltip } from "react-tooltip";
interface PassengerSelectorProps {
    onPassengerSelect: (passengers: string) => void;
    onPassengerDataUpdate?: (data: { adult: number; senior: number; child: number; special_assistance: boolean; special_assistance_reason?: string }) => void;
    onClose: () => void;
    triggerElement: HTMLElement | null;
    currentPassengers: string;
}

interface PassengerCounts {
    adult: number;
    senior: number;
    child: number;
}

const PassengerSelector = ({ onPassengerSelect, onPassengerDataUpdate, onClose, triggerElement, currentPassengers }: PassengerSelectorProps) => {
    const [position, setPosition] = useState({ top: 0, left: 0 })
    const [isPositioned, setIsPositioned] = useState(false)
    const [sameEachWay, setSameEachWay] = useState(true)
    const [specialAssistance, setSpecialAssistance] = useState(false)
    const [showAssistanceDropdown, setShowAssistanceDropdown] = useState(false)
    const [selectedAssistance, setSelectedAssistance] = useState<string>('')
    const selectorRef = useRef<HTMLDivElement>(null)

    const assistanceOptions = [
        'I have a guide dog',
        'I\'m in a wheelchair of my own',
        'Others'
    ]

    // Parse current passengers or set defaults
    const [passengers, setPassengers] = useState<PassengerCounts>(() => {
        // Allow passengers to start at 0
        const total = parseInt(currentPassengers) || 0
        return {
            adult: total,
            senior: 0,
            child: 0
        }
    })

    // Calculate position based on trigger element
    useEffect(() => {
        if (triggerElement) {
            // Small delay to ensure smooth positioning
            requestAnimationFrame(() => {
                const rect = triggerElement.getBoundingClientRect()
                const scrollTop = window.pageYOffset || document.documentElement.scrollTop
                const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft
                const viewportWidth = window.innerWidth
                const viewportHeight = window.innerHeight

                // Estimate dropdown width based on screen size
                let dropdownWidth = 400 // default for xl screens
                if (viewportWidth < 640) dropdownWidth = viewportWidth * 0.95 // sm: 95vw
                else if (viewportWidth < 768) dropdownWidth = viewportWidth * 0.8 // md: 80vw
                else if (viewportWidth < 1024) dropdownWidth = viewportWidth * 0.6 // lg: 60vw
                else if (viewportWidth < 1280) dropdownWidth = viewportWidth * 0.4 // xl: 40vw
                else dropdownWidth = Math.min(viewportWidth * 0.3, 448) // xl: 30vw, max 448px

                let left = rect.left + scrollLeft
                let top = rect.bottom + scrollTop + 4

                // Adjust horizontal position if dropdown would overflow
                if (left + dropdownWidth > viewportWidth) {
                    left = Math.max(8, viewportWidth - dropdownWidth - 8) // 8px margin from edge
                }

                // Adjust vertical position if dropdown would overflow
                const estimatedHeight = 500 // estimated dropdown height
                const spaceBelow = viewportHeight - (rect.bottom - scrollTop)
                const spaceAbove = rect.top - scrollTop

                // Only move above if there's insufficient space below AND enough space above
                if (spaceBelow < estimatedHeight && spaceAbove > estimatedHeight) {
                    top = rect.top + scrollTop - estimatedHeight - 4 // Show above trigger
                }
                // Otherwise keep it below (default behavior)

                setPosition({ top, left })
                setIsPositioned(true)
            })
        }
    }, [triggerElement])

    // Close selector when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (selectorRef.current && !selectorRef.current.contains(event.target as Node)) {
                // Also check if the click was on the trigger element
                if (triggerElement && !triggerElement.contains(event.target as Node)) {
                    onClose()
                }
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [onClose, triggerElement])

    const updatePassenger = (type: keyof PassengerCounts, increment: boolean) => {
        setPassengers(prev => {
            const newCount = increment ? prev[type] + 1 : Math.max(0, prev[type] - 1)
            const updated = { ...prev, [type]: newCount }

            // Allow passengers to be 0 (removed the minimum adult requirement)

            // Update the input field immediately
            const total = updated.adult + updated.senior + updated.child
            onPassengerSelect(total.toString())

            // Schedule Redux update for next tick to avoid setState during render
            setTimeout(() => {
                onPassengerDataUpdate?.({
                    adult: updated.adult,
                    senior: updated.senior,
                    child: updated.child,
                    special_assistance: specialAssistance,
                    special_assistance_reason: selectedAssistance
                })
            }, 0)

            return updated
        })
    }

    const getTotalPassengers = () => {
        return passengers.adult + passengers.senior + passengers.child
    }

    const handleAssistanceSelect = (option: string) => {
        setSelectedAssistance(option)
        setShowAssistanceDropdown(false)

        // Schedule Redux update for next tick to avoid setState during render
        setTimeout(() => {
            onPassengerDataUpdate?.({
                adult: passengers.adult,
                senior: passengers.senior,
                child: passengers.child,
                special_assistance: specialAssistance,
                special_assistance_reason: option
            })
        }, 0)
    }

    const handleApply = () => {
        onClose()
    }

    const selectorContent = isPositioned ? (
        <div
            ref={selectorRef}
            className="absolute font-work-sans bg-white border border-gray-300 rounded-lg shadow-xl z-[9999] p-6 w-[95vw] sm:w-[80vw] md:w-[60vw] lg:w-[40vw] xl:w-[30vw] max-w-md"
            style={{
                top: `${position.top}px`,
                left: `${position.left}px`
            }}
        >
            <h3 className="text-xl font-bold font-rakesly text-gray-900 mb-2">NUMBER OF PASSENGERS</h3>
            <p className="text-gray-600 mb-4">Please enter the number of passengers.</p>



            {/* Passenger types */}
            <div className="space-y-4 mb-6">
                {/* Adult */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                        <h4 className="font-semibold text-gray-900">Adult</h4>
                        <p className="text-sm text-gray-600">16+</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => updatePassenger('adult', false)}
                            className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100"
                        >
                            −
                        </button>
                        <span className="w-8 text-center font-medium">{passengers.adult}</span>
                        <button
                            onClick={() => updatePassenger('adult', true)}
                            className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100"
                        >
                            +
                        </button>
                    </div>
                </div>

                {/* Senior/Student */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                        <h4 className="font-semibold text-gray-900">Senior / Student</h4>
                        <p className="text-sm text-gray-600">55+ or student with ID</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => updatePassenger('senior', false)}
                            className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100"
                        >
                            −
                        </button>
                        <span className="w-8 text-center font-medium">{passengers.senior}</span>
                        <button
                            onClick={() => updatePassenger('senior', true)}
                            className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100"
                        >
                            +
                        </button>
                    </div>
                </div>

                {/* Child */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                        <h4 className="font-semibold text-gray-900">Child</h4>
                        <p className="text-sm text-gray-600">4-15</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => updatePassenger('child', false)}
                            className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100"
                        >
                            −
                        </button>
                        <span className="w-8 text-center font-medium">{passengers.child}</span>
                        <button
                            onClick={() => updatePassenger('child', true)}
                            className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100"
                        >
                            +
                        </button>
                    </div>
                </div>
            </div>

            {/* Special assistance toggle */}
            <div className="mb-6">
                <div className="flex items-center gap-3 mb-3">
                    <button
                        onClick={() => {
                            const newSpecialAssistance = !specialAssistance
                            setSpecialAssistance(newSpecialAssistance)
                            if (!newSpecialAssistance) {
                                setShowAssistanceDropdown(false)
                                setSelectedAssistance('')
                            }

                            // Schedule Redux update for next tick to avoid setState during render
                            setTimeout(() => {
                                onPassengerDataUpdate?.({
                                    adult: passengers.adult,
                                    senior: passengers.senior,
                                    child: passengers.child,
                                    special_assistance: newSpecialAssistance,
                                    special_assistance_reason: newSpecialAssistance ? selectedAssistance : ''
                                })
                            }, 0)
                        }}
                        className={`relative w-12 h-6 rounded-full transition-colors ${specialAssistance ? 'bg-red-500' : 'bg-gray-300'
                            }`}
                    >
                        <div
                            className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${specialAssistance ? 'translate-x-6' : 'translate-x-0.5'
                                }`}
                        />
                    </button>
                    <span className="text-gray-700">Special assistance required</span>

                    <div data-tooltip-id="my-tooltip-1" className="w-5 h-5 rounded-full border border-gray-400 flex items-center justify-center text-xs text-gray-600 cursor-help hover:border-gray-600 transition-colors">
                        ?
                    </div>


                    <ReactTooltip
                        className='max-w-[300px]'
                        id="my-tooltip-1"
                        place="bottom"
                        content="Select this option if you need special assistance such as wheelchair access, guide dog accommodation, or other accessibility services during your journey."
                    />
                </div>

                {/* Special assistance dropdown */}
                {specialAssistance && (
                    <div className="relative">
                        <button
                            onClick={() => setShowAssistanceDropdown(!showAssistanceDropdown)}
                            className="w-full p-3 border border-gray-300 rounded-md bg-white text-left flex items-center justify-between hover:border-gray-400 transition-colors"
                        >
                            <span className="text-gray-700">
                                {selectedAssistance || 'Select which assistance is required'}
                            </span>
                            <Image
                                src={ICONS.downarrow}
                                alt='dropdown arrow'
                                height={15}
                                width={15}
                                className={`transition-transform duration-200 ${showAssistanceDropdown ? 'rotate-180' : 'rotate-0'}`}
                            />
                        </button>

                        {showAssistanceDropdown && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-48 overflow-y-auto">
                                {assistanceOptions.map((option, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer"
                                        onClick={() => handleAssistanceSelect(option)}
                                    >
                                        <span className="text-gray-700">{option}</span>
                                        {selectedAssistance === option && (
                                            <Image src={ICONS.tick} alt='tick' height={15} width={15} />
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>


        </div>
    ) : null

    return typeof window !== 'undefined' && selectorContent
        ? createPortal(selectorContent, document.body)
        : null
}

export default PassengerSelector